'use client';

import { useTranslations } from 'next-intl';
import { type UIEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from '@/app/components/ui/use-toast';
import { useClawAccess, useFetch } from '@/app/hooks';
import { useStore, useStoreApi } from '@/app/providers/store';
import type { TextPromptWithInputItem } from '@/app/types';
import { useRouter } from '@/i18n/routing';
import { useApplyForViduClaw } from '../../_hook/use-apply-for-vidu-claw';
import { useChatDropzone } from '../../_hook/use-chat-dropzone';
import { useChatHistory } from '../../_hook/use-chat-history';
import { useChatStream } from '../../_hook/use-chat-stream';
import { useClawFolder } from '../../_hook/use-claw-folder';
import { useClawInactiveHandler } from '../../_hook/use-claw-inactive-handler';
import { useSessionDetail } from '../../_hook/use-session-detail';
import { useShareSelection } from '../../_hook/use-share-selection';
import { useStoryActions } from '../../_hook/use-story-actions';
import { useUploadClawFile } from '../../_hook/use-upload';
import { useUploadWithRenameOnConflict } from '../../_hook/use-upload-with-rename-on-conflict';
import { DEFAULT_MEDIA_ROOT } from '../../_utils';
import { clawsCreateSession } from '../../apis';
import type { ChatMessageItem, ClawSendFile, ConversationTurnPhase, SessionMode } from '../../types';
import { type AnchorItem, buildAnchorSelector, useChatAnchors } from '../Conversation/hooks/use-chat-anchors';
import { useStickyBottom } from '../Conversation/hooks/use-sticky-bottom';
import type { ChatInputRef } from './ChatInput';
import { normalizeFilePath } from './chat-scene-paths';
import { createChatSceneSendHandler } from './chat-scene-send';
import { deriveIsAwaitingFirstMessageAfterCreate } from './derive-awaiting-first-message-flag';
import { deriveChatInputState } from './derive-chat-input-state';
import type { MessageListHandle } from './MessageList';
import { useSessionMediaRestore } from './use-session-media-restore';

const AUTO_SCROLL_BOTTOM_THRESHOLD = 80;

// typer.onEnd 是主信号；此值仅作 fail-safe（onEnd 未触发时兜底）。
// ClawMarkdown 尾段渲染可达 ~2s，4000ms 留足缓冲。
const TYPING_IDLE_MS = 4000;

const IDLE_PHASE: ConversationTurnPhase = { status: 'idle' };

export type ExpandedChatPanel = 'subtask' | null;

export const useChatScene = ({ sessionsId }: { sessionsId?: string }) => {
  const t = useTranslations();
  const router = useRouter();
  const storeApi = useStoreApi();
  const [messages, setMessages] = useState<ChatMessageItem[]>([]);
  const [pendingPreviewFilePath, setPendingPreviewFilePath] = useState<string | null>(null);
  // 乐观新建的 session 首条消息发送失败（内容审核未通过 / 暂存附件上传失败）时记下其 id：
  // 用来结束 deriveIsAwaitingFirstMessageAfterCreate 的等待态，让页面落到空态而非永久 loading。
  const [firstSendAbortedSessionId, setFirstSendAbortedSessionId] = useState<string | null>(null);
  const [expandedPanel, setExpandedPanel] = useState<ExpandedChatPanel>(null);
  const chatContainerRef = useRef<HTMLDivElement | null>(null);
  const chatScrollRef = useRef<HTMLDivElement | null>(null);
  const messageListRef = useRef<MessageListHandle | null>(null);
  const shouldAutoScrollRef = useRef(true);
  const prevHistoryBootstrappingRef = useRef(false);
  const lastScrollTopRef = useRef(0);
  const chatInputRef = useRef<ChatInputRef>(null);
  // assistantMessageId → 草稿快照，发送失败时还原输入框。
  const sendDraftSnapshotRef = useRef<
    Map<string, { inputValue?: TextPromptWithInputItem; text?: string; files: ClawSendFile[] }>
  >(new Map());
  const refreshAfterGenerationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const historyAccessErrorHandledRef = useRef(false);
  const disableRouteSessionSyncRef = useRef(false);
  // 本端刚新建、尚未经 useSessionDetail 校验的 session id。
  // 用它绕过 detail 门控：既然是自己刚建的就无需存在性校验，
  // 也避免 detail fetch 期间 useChatHistory 收到 null 清掉乐观写入的占位。
  const newlyCreatedSessionIdsRef = useRef<Set<string>>(new Set());
  // 对话轮次生命周期：idle → streaming(id) → typing(id) → idle。
  // ref 供高频回调（measure / scroll）读取，state 驱动 UI 渲染。
  const turnPhaseRef = useRef<ConversationTurnPhase>(IDLE_PHASE);
  const [turnPhase, setTurnPhase] = useState<ConversationTurnPhase>(IDLE_PHASE);
  const setTurnPhaseSync = useCallback((next: ConversationTurnPhase) => {
    turnPhaseRef.current = next;
    setTurnPhase(next);
  }, []);

  const {
    sessions,
    activeSessionId,
    activeClaw,
    clawStatus,
    isShowFile,
    clawFolderPath,
    prependSession,
    setActiveSessionId,
    setClawRightPanelOpen,
    bumpClawFileListRefreshKey,
    setClawFolderPath,
    setSessionMediaFolder,
    removeSessionMediaFolder,
    sessionMediaFolderMap,
  } = useStore((state) => ({
    sessions: state.sessions,
    activeSessionId: state.activeSessionId,
    activeClaw: state.activeClaw,
    clawStatus: state.clawStatus,
    isShowFile: state.clawRightPanelOpen,
    clawFolderPath: state.clawFolderPath,
    prependSession: state.prependSession,
    setActiveSessionId: state.setActiveSessionId,
    setClawRightPanelOpen: state.setClawRightPanelOpen,
    bumpClawFileListRefreshKey: state.bumpClawFileListRefreshKey,
    setClawFolderPath: state.setClawFolderPath,
    setSessionMediaFolder: state.setSessionMediaFolder,
    removeSessionMediaFolder: state.removeSessionMediaFolder,
    sessionMediaFolderMap: state.sessionMediaFolderMap,
  }));

  const { createFolder: createClawFolderHook, listFolder: listClawFolder } = useClawFolder(activeClaw?.id);
  const { uploadClawFile } = useUploadClawFile();
  const { uploadWithRenameOnConflict } = useUploadWithRenameOnConflict();
  // access 在聊天场景中决定 dropzone 与 ChatInput 的上传策略：
  // - ready：直传 claw
  // - entitled / expired：本地暂存为 pending 草稿，开通后由 pendingFiles 通道补传
  // - null：不允许拖入，避免短暂窗口把文件丢进无人接收的状态
  const { access } = useClawAccess();
  const allowFileIntake = access === 'ready' || access === 'entitled' || access === 'expired';

  // session detail 前置：detail 就绪前阻塞 history 拉取，确保存在性校验先于消息加载。
  // 例外见上方 newlyCreatedSessionIdsRef——刚新建的 session 直接放行。
  const {
    sessionDetail,
    isReady: isSessionDetailReady,
    isNotFound: isSessionNotFound,
  } = useSessionDetail(activeSessionId);

  const isNewlyCreatedSession = !!activeSessionId && newlyCreatedSessionIdsRef.current.has(activeSessionId);

  // detail 校验完成后清掉新建登记，避免永久残留。
  useEffect(() => {
    if (isSessionDetailReady && activeSessionId) {
      newlyCreatedSessionIdsRef.current.delete(activeSessionId);
    }
  }, [isSessionDetailReady, activeSessionId]);

  // 当前 session 的 mode：优先取 detail 接口（最新），fallback 到 sessions 列表。
  // useChatStream 用它判断非 default 模式下是否在首条 message.created 时主动收尾流。
  const activeSessionMode = useMemo(() => {
    if (!activeSessionId) return undefined;
    if (sessionDetail?.mode) return sessionDetail.mode;
    return sessions.find((item) => item.id === activeSessionId)?.mode;
  }, [activeSessionId, sessionDetail, sessions]);

  // run_phase 由 use-subscribe-chat-state（live）和 use-session-detail（initial）写入 store。
  // 传给 useChatStream，在 turn.completed 丢失时兜底收掉卡住的思考中状态。
  const activeRunPhase = useMemo(
    () => sessions.find((item) => item.id === activeSessionId)?.run_phase,
    [activeSessionId, sessions],
  );

  const handleDropFiles = useCallback((files: File[], skippedDirectoryCount: number) => {
    if (skippedDirectoryCount > 0) {
      toast({
        type: 'info',
        message: t('claw.upload.directoriesIgnored', { count: skippedDirectoryCount }),
      });
    }
    if (files.length > 0) chatInputRef.current?.addFiles(files);
  }, []);

  const { isDraggingOver } = useChatDropzone({
    containerRef: chatContainerRef,
    onDrop: handleDropFiles,
    enabled: allowFileIntake,
  });

  const { runAsync: createSessionAsync, loading: creatingSession } = useFetch(
    ({ clawId, name, mode }: { clawId: string; name: string; mode?: SessionMode }) =>
      clawsCreateSession({
        body: {
          claw_id: clawId,
          name,
          ...(mode ? { mode } : {}),
        },
      }),
    {
      manual: true,
    },
  );

  const { handleClawInactiveError } = useClawInactiveHandler();

  const hasActiveTask = useMemo(() => {
    if (!activeSessionId) return false;
    return (sessions.find((item) => item.id === activeSessionId)?.active_task_count ?? 0) > 0;
  }, [activeSessionId, sessions]);

  // 打破 useChatHistory 与 useChatStream 的循环依赖：
  // - useChatHistory 的 skip-hydration 判断依赖 useChatStream 的 refs
  // - useChatStream 的 lastStreamSeq 来自 useChatHistory
  const skipHydrationRef = useRef<() => boolean>(() => false);
  const shouldSkipHistoryHydration = useCallback(() => skipHydrationRef.current(), []);
  const currentReadStreamSeqRef = useRef<string | null | undefined>(undefined);

  const setCurrentReadStreamSeq = useCallback((streamSeq: string | null | undefined) => {
    currentReadStreamSeqRef.current = streamSeq;
  }, []);

  const getCurrentReadStreamSeq = useCallback(() => currentReadStreamSeqRef.current, []);

  const replaceClawUrlWithoutNavigation = useCallback((sessionId?: string) => {
    if (typeof window === 'undefined') return;
    const { pathname, search, hash } = window.location;
    const normalized = pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
    const next = normalized.replace(/\/claw(?:\/[^/]+)?$/, sessionId ? `/claw/${sessionId}` : '/claw');
    if (next !== normalized) window.history.replaceState(window.history.state, '', `${next}${search}${hash}`);
  }, []);

  const handleHistoryAccessError = useCallback(
    (error: { code: number; reason: string }) => {
      if (historyAccessErrorHandledRef.current) return;
      historyAccessErrorHandledRef.current = true;
      disableRouteSessionSyncRef.current = true;

      const isSessionNotFound = error.code === 404 || error.reason === 'SessionNotFound';
      const isForbidden = error.code === 403 || error.reason === 'Forbidden';
      toast({
        type: 'error',
        message: isSessionNotFound
          ? t('claw.chat.sessionNotFound')
          : isForbidden
            ? t('claw.chat.sessionForbidden')
            : t('claw.chat.error.failed'),
      });

      setActiveSessionId(null);
      setMessages([]);
      router.replace('/claw');
    },
    [router, setActiveSessionId],
  );

  // session detail 未就绪时传 null 阻塞 history 加载，避免在 session 校验完成前发起无效请求
  const { isHistoryBootstrapping, historyLoading, lastStreamSeq, runningTurn, markAsNewSession } = useChatHistory({
    activeSessionId: isSessionDetailReady || isNewlyCreatedSession ? activeSessionId : null,
    shouldBootstrapInitially: !!sessionsId && isSessionDetailReady,
    shouldSkipHydration: shouldSkipHistoryHydration,
    getCurrentReadStreamSeq,
    setCurrentReadStreamSeq,
    setMessages,
    onHistoryAccessError: handleHistoryAccessError,
    sessionMode: activeSessionMode,
    runPhase: activeRunPhase,
  });

  const { isStreaming, isStreamingRef, assistantMessageIdRef, startChatStream, phases } = useChatStream({
    sessionId: clawStatus === 'CLAW_STATUS_ACTIVE' ? activeSessionId : null,
    sessionMode: activeSessionMode,
    lastStreamSeq,
    onReadStreamSeqChange: setCurrentReadStreamSeq,
    runningTurn,
    runPhase: activeRunPhase,
    setMessages,
    onSendApiError: handleClawInactiveError,
    onSendFailed: ({ assistantMessageId, error }) => {
      const snapshot = sendDraftSnapshotRef.current.get(assistantMessageId);
      sendDraftSnapshotRef.current.delete(assistantMessageId);
      if (!snapshot) return false;

      // 移除这一对 user / assistant 占位
      setMessages((prev) => {
        const ai = prev.findIndex((m) => m.id === assistantMessageId);
        if (ai < 1) return prev;
        return prev.filter((_, i) => i !== ai && i !== ai - 1);
      });

      const restored = chatInputRef.current?.restoreDraft({
        inputValue: snapshot.inputValue,
        text: snapshot.text,
        files: snapshot.files,
      });
      // 命中内容审核 reason=AuditSubmitIllegal
      toast({
        type: 'error',
        message: error?.reason === 'AuditSubmitIllegal' ? t('claw.chat.error.moderation') : t('claw.chat.error.failed'),
      });
      // markStreamCompleted 由 handleSendErrorRef 负责；上层接管后仍需复位 isStreaming。
      return !!restored || true;
    },
  });

  useEffect(() => {
    if (expandedPanel === 'subtask' && phases.length === 0) {
      setExpandedPanel(null);
    }
  }, [expandedPanel, phases.length]);

  skipHydrationRef.current = () => isStreamingRef.current || !!assistantMessageIdRef.current;

  // yolo 收到 storyboard 即视为本轮结束，不走伪打字 idle 兜底，否则输入框会被 isSending 锁 4s。
  const isStoryboardTurnFinished = !!messages[messages.length - 1]?.story;
  const isSending = creatingSession || (!isStoryboardTurnFinished && turnPhase.status !== 'idle');
  // welcome/新建态：sessionsId 和 activeSessionId 都为空才算。
  // 欢迎页发首条消息时只 replaceState 写 URL，sessionsId 不刷新但 activeSessionId 已进 store，
  // 必须一起判断，否则发送瞬间会闪回欢迎态。不用 messages.length === 0：session 已建但首条未成功 / 历史未回时也是 0。
  const isNew = !sessionsId && !activeSessionId;

  const shareSelection = useShareSelection({
    activeSessionId,
    messages,
    setMessages,
    ready: !isHistoryBootstrapping && !historyLoading,
  });

  const storyActions = useStoryActions({
    messages,
    setMessages,
    activeSessionId,
    sessionMediaFolderMap,
  });

  // AnchorCon 的输入：从 messages 派生稳定的锚点列表，避免流式期间每帧重算 + 引用抖动。
  const anchors = useChatAnchors(messages, { assistantOutputLabel: t('claw.chat.anchor.assistantOutput') });

  // streaming 阶段把最新 assistant id 同步进 turnPhase，使 streaming(id) 始终指向本轮最后一条 assistant 消息。
  // 找不到 assistant 占位但仍残留旧 typing 时退回 idle，等价于旧的"新流让位"兜底。
  // 放进 effect（而非 render 期 setState）：晚一帧但更稳，只影响高亮 / 展开，用户感知不到。
  useEffect(() => {
    if (!isStreaming) return;
    let latestAssistantId = '';
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      if (messages[i]?.role === 'assistant') {
        latestAssistantId = messages[i]?.id ?? '';
        break;
      }
    }
    if (latestAssistantId) {
      if (
        turnPhaseRef.current.status !== 'streaming' ||
        turnPhaseRef.current.assistantMessageId !== latestAssistantId
      ) {
        setTurnPhaseSync({ status: 'streaming', assistantMessageId: latestAssistantId });
      }
    } else if (turnPhaseRef.current.status === 'typing') {
      setTurnPhaseSync(IDLE_PHASE);
    }
  }, [isStreaming, messages, setTurnPhaseSync]);

  useEffect(() => {
    if (disableRouteSessionSyncRef.current) return;
    // 路由与 store 双向对齐：
    // - /claw/:sessionId => 激活对应会话
    // - /claw => 回到新建对话态
    if (sessionsId) {
      setActiveSessionId(sessionsId);
      return;
    }

    setActiveSessionId(null);
  }, [sessionsId]);

  useEffect(() => {
    shouldAutoScrollRef.current = true;
    historyAccessErrorHandledRef.current = false;
    // session 切换时立即复位 turnPhase。
    setTurnPhaseSync(IDLE_PHASE);
    // 切到别的 session 时清掉上一个 session 的「首条发送失败」标记，避免误挂到新 session 上。
    setFirstSendAbortedSessionId((prev) => (prev === activeSessionId ? prev : null));
  }, [activeSessionId, setTurnPhaseSync]);

  // session 不存在时走统一的错误回退：toast 提示 + 清空 session + URL 回退到 /claw。
  useEffect(() => {
    if (!isSessionNotFound) return;
    handleHistoryAccessError({ code: 404, reason: 'SessionNotFound' });
  }, [isSessionNotFound, handleHistoryAccessError]);

  const scrollToBottom = useCallback(() => {
    requestAnimationFrame(() => {
      if (!chatScrollRef.current) return;
      chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
      lastScrollTopRef.current = chatScrollRef.current.scrollTop;
    });
  }, []);

  const handleChatScroll = useCallback((event: UIEvent<HTMLDivElement>) => {
    const target = event.currentTarget;
    const currentScrollTop = target.scrollTop;
    const distanceToBottom = target.scrollHeight - target.scrollTop - target.clientHeight;
    const isNearBottom = distanceToBottom <= AUTO_SCROLL_BOTTOM_THRESHOLD;
    const isScrollingUp = currentScrollTop < lastScrollTopRef.current - 1;

    if (isNearBottom) {
      shouldAutoScrollRef.current = true;
    } else if (isScrollingUp) {
      shouldAutoScrollRef.current = false;
    }

    lastScrollTopRef.current = currentScrollTop;
  }, []);

  useEffect(() => {
    // bootstrap 阶段交给下面那个 effect 用 virtualizer 对齐，避免两次 scroll 抢。
    if (isHistoryBootstrapping) {
      return;
    }
    const willScroll = shouldAutoScrollRef.current || isNew || historyLoading || creatingSession;
    if (willScroll) {
      scrollToBottom();
    }
  }, [messages, isNew, historyLoading, creatingSession, isHistoryBootstrapping, isStreaming, scrollToBottom]);

  // 历史消息加载完成后，交给 MessageList virtualizer 滚到末尾。
  // 它的 RAF 补齐循环会持续用 getOffsetForIndex 对齐真实 offset，
  // 避免估算 totalSize 下 scrollHeight 提前稳定导致没滚到底。
  useEffect(() => {
    const wasBootstrapping = prevHistoryBootstrappingRef.current;
    prevHistoryBootstrappingRef.current = isHistoryBootstrapping;

    if (!(wasBootstrapping && !isHistoryBootstrapping) || messages.length === 0) {
      return;
    }

    shouldAutoScrollRef.current = true;

    // 延一帧等 MessageList ref 挂载并首屏渲染完毕。
    const rafId = requestAnimationFrame(() => {
      messageListRef.current?.scrollToMessageIndex(messages.length - 1, {
        align: 'end',
        stickyBottomWatch: true,
      });
    });

    return () => {
      cancelAnimationFrame(rafId);
    };
  }, [isHistoryBootstrapping, messages.length]);

  useEffect(() => {
    // streaming 真正结束（true→false）的那一刻才收尾；turnPhase 仍停留在 streaming 即代表上一轮在流式。
    if (isStreaming || turnPhaseRef.current.status !== 'streaming') {
      return;
    }

    // streaming 结束这一刻：assistant 还有 typer 内容则进入 typing 阶段，由 measure 增长做 idle 判定 + onEnd 解锁。
    // useStickyBottom 在 active=true 时自行初始化 lastActivity 时间戳，给 typer 至少 idleMs 的起步窗口。
    const lastAssistantId = turnPhaseRef.current.assistantMessageId ?? '';
    const lastAssistantMessage = lastAssistantId ? messages.find((m) => m.id === lastAssistantId) : null;
    const hasTyperContent = !!(lastAssistantMessage?.content?.trim() || lastAssistantMessage?.thinking?.trim());
    // story-only 的 turn 没有 typer（StoryMessageCard 不会触发 onEnd，typing 永不解锁），直接回 idle。
    if (lastAssistantId && hasTyperContent && !lastAssistantMessage?.story) {
      setTurnPhaseSync({ status: 'typing', assistantMessageId: lastAssistantId });
    } else {
      setTurnPhaseSync(IDLE_PHASE);
    }

    // 流式结束后 assistant 消息还有一次 measure 回填（markdown/thinking 真实高度）。
    // 此时 messages 引用不变，autoScroll effect 不跑，scrollToBottom 估算的 scrollHeight 也不再更新。
    // 这里显式委托 virtualizer 对齐到末尾，stickyBottomWatch 兜住 400ms 的 measure 余震。
    if (shouldAutoScrollRef.current && messages.length > 0) {
      requestAnimationFrame(() => {
        messageListRef.current?.scrollToMessageIndex(messages.length - 1, {
          align: 'end',
          stickyBottomWatch: true,
        });
      });
    }

    bumpClawFileListRefreshKey();

    if (refreshAfterGenerationTimerRef.current) {
      clearTimeout(refreshAfterGenerationTimerRef.current);
    }

    refreshAfterGenerationTimerRef.current = setTimeout(() => {
      bumpClawFileListRefreshKey();
      refreshAfterGenerationTimerRef.current = null;
    }, 1200);
  }, [isStreaming]);

  useEffect(() => {
    return () => {
      if (refreshAfterGenerationTimerRef.current) {
        clearTimeout(refreshAfterGenerationTimerRef.current);
      }
    };
  }, []);

  const handlePendingTypingIdle = useCallback(() => {
    if (turnPhaseRef.current.status !== 'typing') return;
    setTurnPhaseSync(IDLE_PHASE);
  }, []);

  // typing 期间常驻 sticky-bottom + idle 兜底解锁：
  // - shouldAutoScrollRef=true 时每帧贴底，否则让位给用户自己滚
  // - 连续 TYPING_IDLE_MS 没收到 markActivity 调用即视为 typer 收尾，触发 onIdle
  const { markActivity: markPendingTypingActivity } = useStickyBottom({
    scrollRef: chatScrollRef,
    active: turnPhase.status === 'typing',
    userInteractionPolicy: 'follow-flag',
    shouldPin: () => shouldAutoScrollRef.current,
    idleMs: TYPING_IDLE_MS,
    onIdle: handlePendingTypingIdle,
  });

  // MessageList 上报"该 assistant item 高度又涨了"——typer 仍在按帧吐字的地面真相。
  // 不依赖 state，回调引用稳定，避免 ResizeObserver 监听链上不必要的重连。
  const handleAssistantMeasureGrow = useCallback(
    (messageId: string) => {
      if (turnPhaseRef.current.status !== 'typing' || turnPhaseRef.current.assistantMessageId !== messageId) {
        return;
      }
      markPendingTypingActivity();
    },
    [markPendingTypingActivity],
  );

  // typer 真正打完的精准信号，由 AssistantMessage 在 streaming 边沿后等下一次 onEnd 上报。
  // 命中即立刻收回 idle，比 idle 阈值更快、更稳。idle monitor 仅作兜底。
  const handleAssistantTypingEnd = useCallback((messageId: string) => {
    if (turnPhaseRef.current.status !== 'typing' || turnPhaseRef.current.assistantMessageId !== messageId) {
      return;
    }
    setTurnPhaseSync(IDLE_PHASE);
  }, []);

  const chatInputOnSend = createChatSceneSendHandler({
    activeSessionId,
    getActiveClawId: () => storeApi.getState().activeClaw?.id,
    clawFolderPath,
    sessionMediaFolderMap,
    createSessionAsync,
    handleClawInactiveError,
    prependSession,
    replaceClawUrlWithoutNavigation,
    markAsNewSession,
    newlyCreatedSessionIdsRef,
    setActiveSessionId,
    createClawFolder: createClawFolderHook,
    setClawFolderPath,
    setSessionMediaFolder,
    bumpClawFileListRefreshKey,
    uploadClawFile,
    uploadWithRenameOnConflict,
    setMessages,
    sendDraftSnapshotRef,
    startChatStream,
    shouldAutoScrollRef,
    onFirstSendAfterCreateAborted: setFirstSendAbortedSessionId,
  });

  // 未开通（entitled / expired）态在输入框点发送：先 await 开通（createClawByCode + 本地轮询），
  // 开通成功后由 ChatInput 的 chatInputOnSend 在同一次点击流程内继续真正发送（建 session / 传附件 / startChatStream）。
  // 不再用「access 变 ready 后监听补发」的方式，改为命令式串联，逻辑更线性、可控。
  const { applyForViduClaw, applying: applyingForViduClaw } = useApplyForViduClaw();

  // 欢迎页发首条消息切到对话态时的窗口：ConversationChatPanel 已 mount 但 user 占位还没塞进来
  // （POST /sessions → 建目录 → 传文件 → setMessages），此时 messages 为空且新 session 不需 bootstrap，
  // 原本会闪一帧 ChatEmptySession。派生 flag 让该窗口走 ChatLoading；判定收敛在纯函数里方便测试。
  const isAwaitingFirstMessageAfterCreate = deriveIsAwaitingFirstMessageAfterCreate({
    sessionsId,
    activeSessionId,
    creatingSession,
    messagesLength: messages.length,
    firstSendAbortedSessionId,
  });
  const {
    isReady,
    showChatInput,
    disableChatInputSend,
    disableChatInputUpload,
    chatInputSending,
    chatInputYoloActive,
  } = deriveChatInputState({
    access,
    activeClawId: activeClaw?.id,
    activeClawStatus: activeClaw?.status,
    activeSessionId,
    activeSessionMode,
    isSending,
    applyingForViduClaw,
  });
  const handlePreviewFile = useCallback(
    (filePath: string) => {
      const normalizedPath = normalizeFilePath(filePath);
      if (!normalizedPath) return;
      const folderPath = normalizedPath.split('/').slice(0, -1).join('/') || DEFAULT_MEDIA_ROOT;
      setClawFolderPath(folderPath.includes(DEFAULT_MEDIA_ROOT) ? folderPath : DEFAULT_MEDIA_ROOT);
      setClawRightPanelOpen(true);
      setPendingPreviewFilePath(normalizedPath);
      bumpClawFileListRefreshKey();
    },
    [setClawFolderPath, setClawRightPanelOpen, bumpClawFileListRefreshKey],
  );

  const handleQuoteFile = useCallback((filePath: string) => {
    const normalizedPath = normalizeFilePath(filePath);
    if (!normalizedPath) return;
    const name = normalizedPath.split('/').filter(Boolean).pop() ?? normalizedPath;
    chatInputRef.current?.insertAssetMention({ path: normalizedPath, name });
  }, []);

  // assistant 锚点要落在 fileList（产物）位置而非消息顶部，所以再补一个 sub-target selector 做 fineTune。
  const handleAnchorJump = useCallback((anchor: AnchorItem) => {
    messageListRef.current?.scrollToMessageIndex(anchor.messageIndex, {
      align: 'start',
      subTargetSelector: anchor.role === 'assistant' ? buildAnchorSelector(anchor.id, 'assistant') : undefined,
    });
  }, []);

  const { restoringSessionMedia } = useSessionMediaRestore({
    activeSessionId,
    activeSessionMode,
    clawFolderPath,
    sessionMediaFolderMap,
    setClawFolderPath,
    setSessionMediaFolder,
    removeSessionMediaFolder,
    listClawFolder,
  });

  return {
    // refs
    chatContainerRef,
    chatScrollRef,
    messageListRef,
    chatInputRef,
    // 场景
    isNew,
    // store-derived
    activeSessionId,
    activeClaw,
    isShowFile,
    sessionMediaFolderMap,
    bumpClawFileListRefreshKey,
    // history / stream / message state
    isHistoryBootstrapping,
    historyLoading,
    isAwaitingFirstMessageAfterCreate,
    turnPhase,
    messages,
    phases,
    hasActiveTask,
    expandedPanel,
    setExpandedPanel,
    pendingPreviewFilePath,
    setPendingPreviewFilePath,
    restoringSessionMedia,
    // story actions
    ...storyActions,
    // dropzone
    isDraggingOver,
    // chat input contract
    chatInputSending,
    disableChatInputSend,
    disableChatInputUpload,
    chatInputYoloActive,
    showChatInput,
    isReady,
    chatInputOnSend,
    onApplyForAccess: applyForViduClaw,
    // anchors
    anchors,
    // handlers
    handleChatScroll,
    handlePreviewFile,
    handleQuoteFile,
    handleAnchorJump,
    handleAssistantMeasureGrow,
    handleAssistantTypingEnd,
    // share selection
    ...shareSelection,
  };
};

export type ChatSceneCtx = ReturnType<typeof useChatScene>;
